import objectPath from 'object-path'
import { persistReducer } from 'redux-persist'
import storage from 'redux-persist/lib/storage'

import LayoutConfig from '../layout/LayoutConfig'
import { getMenuConfig } from '../layout/MenuConfig'

export const actionTypes = {
  SetMenuConfig: 'builder/SET_MENU_CONFIG',
  SetLayoutConfigs: 'builder/SET_LAYOUT_CONFIGS',
  SetLayoutConfigsWithPageRefresh: 'builder/SET_LAYOUT_CONFIGS_WITH_PAGE_REFRESH',
  SetHtmlClassService: 'builder/SET_HTML_CLASS_SERVICE',
  SetSubHeaderDisplay: 'builder/SET_SUBHEADER_DISPLAY',
}

/**
 * Resolves `layoutConfig.self.logo` for the current `document.documentElement` color mode.
 * Used by Redux selectors and by layout components on `BS_THEME_CHANGE_EVENT` (no store update on theme toggle).
 */
export function resolveLayoutLogoUrl(layoutConfig) {
  const logoObject = objectPath.get(layoutConfig, 'self.logo')
  if (typeof logoObject === 'string') {
    return logoObject
  }
  if (typeof logoObject !== 'object' || logoObject === null) {
    return undefined
  }
  const isAppDark = typeof document !== 'undefined' && document.documentElement.getAttribute('data-bs-theme') === 'dark'
  const logoKey = isAppDark ? 'dark' : 'light'
  let logo = objectPath.get(logoObject, logoKey)
  if (logo == null) {
    const menuAsideLeftSkin = objectPath.get(layoutConfig, 'brand.self.skin')
    logo = objectPath.get(logoObject, `${menuAsideLeftSkin}`)
  }
  if (typeof logo === 'undefined') {
    try {
      const keys = Object.keys(logoObject)
      logo = keys.length ? logoObject[keys[0]] : undefined
    } catch (e) {
      console.error(e) // eslint-disable-line no-console
    }
  }
  return logo
}

export const selectors = {
  getClasses: (store, params) => {
    const { htmlClassServiceObjects } = store.builder

    return htmlClassServiceObjects ? htmlClassServiceObjects.getClasses(params.path, params.toString) : ''
  },
  getAttributes: (store, params) => {
    if (params.path) {
      // if path is specified, get the value within object
      const { htmlClassServiceObjects } = store.builder

      return htmlClassServiceObjects ? htmlClassServiceObjects.getAttributes(params.path) : []
    }

    return []
  },
  getConfig: (state, path) => {
    const { layoutConfig } = state.builder

    if (path) {
      // if path is specified, get the value within object
      return objectPath.get(layoutConfig, path)
    }

    return ''
  },

  getLogo: ({ builder: { layoutConfig } }) => resolveLayoutLogoUrl(layoutConfig),

  getFavicon: ({ builder: { layoutConfig } }) => {
    const menuAsideLeftSkin = objectPath.get(layoutConfig, 'brand.self.skin')
    // set brand logo
    const faviconObject = objectPath.get(layoutConfig, 'self.favicon')
    let favicon

    if (typeof faviconObject === 'object') {
      favicon = objectPath.get(faviconObject, `${menuAsideLeftSkin}`)
    }
    return favicon
  },

  getStickyLogo: ({ builder: { layoutConfig, branding } }, defaultLogo) => {
    const { publish_logo: publishLogo } = branding?.data ?? {}
    let logo = objectPath.get(layoutConfig, 'self.logo.sticky')
    if (!publishLogo || typeof logo === 'undefined') {
      logo = defaultLogo
    }
    return logo
  },
}

const initialState = {
  menuConfig: getMenuConfig(),
  layoutConfig: LayoutConfig,
  htmlClassServiceObjects: undefined,
}

export const reducer = persistReducer(
  {
    storage,
    key: 'build',
    blacklist: ['menuConfig', 'htmlClassServiceObjects'],
  },
  (state = initialState, { type, payload }) => {
    switch (type) {
      case actionTypes.SetMenuConfig:
        return { ...state, menuConfig: payload }

      case actionTypes.SetLayoutConfigs:
        return { ...state, layoutConfig: payload }

      case actionTypes.SetLayoutConfigsWithPageRefresh: {
        return { ...state, layoutConfig: payload }
      }
      case actionTypes.SetHtmlClassService:
        return { ...state, htmlClassServiceObjects: payload }
      case actionTypes.SetSubHeaderDisplay:
        return {
          ...state,
          layoutConfig: {
            ...state.layoutConfig,
            subheader: {
              ...state.layoutConfig.subheader,
              ...payload,
            },
          },
        }

      default:
        return state
    }
  },
)

export const actions = {
  setMenuConfig: (payload) => ({
    payload,
    type: actionTypes.SetMenuConfig,
  }),

  setLayoutConfigs: (payload) => ({
    payload,
    type: actionTypes.SetLayoutConfigs,
  }),

  setLayoutConfigsWithPageRefresh: (payload) => ({
    payload,
    type: actionTypes.SetLayoutConfigsWithPageRefresh,
  }),

  setHtmlClassService: (payload) => ({
    payload,
    type: actionTypes.SetHtmlClassService,
  }),

  setSubHeaderDisplay: (payload) => ({
    payload,
    type: actionTypes.SetSubHeaderDisplay,
  }),
}
